<template>
  <div class="fill-height of-y d-flex flex-column">
    <v-scroll-x-transition hide-on-leave>
      <app-centre-loader v-if="loading" label="Fetching Data"/>
    </v-scroll-x-transition>

    <div class="d-flex align-center px-8" v-if="!loading">
      <v-btn
        color="primary"
        small
        text
        @click="gotoTeam(team._id)"
      >
        <v-icon left>mdi-account-group</v-icon>
        {{ team.name }}
      </v-btn>
      <template v-for="device in devices">
        <v-btn
          :disabled="!access.setup"
          :key="device._id"
          color="secondary"
          small
          text
          @click="gotoDeviceSettings(device._id)"
        >
          <img src="@/assets/teamsSecondary.svg" style="height: 16px !important;" v-if="devicesRef[device._id].o365TenantUser" class="mr-2">
          <v-icon left v-else>{{!devicesRef[device._id].phone ? 'mdi-cellphone-link' : 'mdi-phone'}}</v-icon>
          {{ device.name }}
        </v-btn>
      </template>
    </div>

    <v-scroll-x-transition hide-on-leave>
      <div class="d-flex align-center justify-center flex-grow-1" v-if="!loading && !stats.topApps.length">
        <div class="text-overline amber--text text-center">
          No data for the selected date range
          <br>
          Please select another date or range
        </div>
      </div>
    </v-scroll-x-transition>

    <v-scroll-x-transition hide-on-leave>
      <div v-if="!loading && stats.topApps.length" class="d-flex flex-column of-y flex-grow-1">
        <div class="flex-grow-1 of-y d-flex flex-column">

          <!--Statistics-->
          <app-divider label="Statistics" show-expanded :expanded.sync="statisticsShown"></app-divider>
          <v-scroll-y-transition hide-on-leave>
            <div class="d-flex align-start justify-space-around flex-wrap px-10 pt-4" v-if="statisticsShown">
              <template v-for="card in shownCards">
                <v-card class="mb-4 mx-2" width="300" :key="card.key" style="position: relative">

                  <div class="pa-2 grey--text" style="position: absolute; right: 0; top: 0">
                    {{ card.valDesc }}
                  </div>

                  <div class="pa-2">
                    <div class="body-2">
                      {{ card.info.text }}
                    </div>
                    <div class="caption grey--text">
                      {{ card.info.description }}
                    </div>
                  </div>

                  <div class="px-4 pb-2 text-h5 text-end">
                    {{ card.val }}
                  </div>
                  <div>
                    {{ card.val2 }}
                  </div>
                </v-card>
              </template>
            </div>
          </v-scroll-y-transition>

          <!--Screenshots-->
          <template v-if="access.screenshots && employee.enableScreenshots">
            <app-divider label="Screenshots" show-expanded :expanded.sync="screenshotsShown"></app-divider>
            <v-scroll-y-transition hide-on-leave>
              <app-screenshot-preview v-if="screenshotsShown" :filter="{employeeIds: [employee._id]}"/>
            </v-scroll-y-transition>
          </template>
          
          <!--Screenshots-->
          <template v-if="access.screenshots && employee.enableScreenshots">
            <app-divider label="Online Devices" show-expanded :expanded.sync="onlineDevicesShown"></app-divider>
            <app-live-view v-if="liveViewDeviceId" :device-id.sync="liveViewDeviceId"></app-live-view>
            <v-scroll-y-transition hide-on-leave>
              <div class="d-flex flex-wrap px-10 pt-4" v-if="onlineDevicesShown">
                <app-online-device-wrapper v-for="device in devices" :key="device._id" :device-id="device._id">
                  <template v-slot:default="{employee, device, isOnline, loading, allowLiveView}">
                    <v-card
                        outlined
                        class="mx-3 my-2 live-view-card"
                        :color="isOnline ? 'success' : 'grey'"
                        dark
                        @click="isOnline && allowLiveView && device._id ? liveViewDeviceId = device._id : () => {}"
                        :style="{ cursor: isOnline ? 'pointer' : 'not-allowed', transition: 'all 0.3s' }"
                        :elevation="isOnline ? 2 : 0"
                        :hover="isOnline"
                        :ripple="isOnline"
                        width="200px"
                    >
                      <v-card-text class="pa-3">
                        <div class="d-flex align-center justify-space-between">
                          <!-- Device Info Section -->
                          <div class="flex-grow-1">
                            <!-- Online Status Indicator -->
                            <div class="d-flex align-center mb-1">
                              <v-icon
                                  small
                                  color="white"
                                  :class="['mr-2', { 'pulse-animation': isOnline }]"
                              >
                                {{ isOnline ? 'mdi-circle' : 'mdi-circle-outline' }}
                              </v-icon>
                              <span class="caption text-uppercase font-weight-bold">
                              {{ isOnline ? 'Online' : 'Offline' }}
                            </span>
                            </div>
                            
                            <!-- Employee Name -->
                            <div class="d-flex align-center" v-if="employee && employee.name">
                              <v-icon small class="mr-1">mdi-account</v-icon>
                              <span class="body-2 font-weight-medium">{{ employee.name }}</span>
                            </div>
                            <div v-else class="d-flex align-center">
                              <v-icon small class="mr-1">mdi-account-off</v-icon>
                              <span class="body-2 text--secondary">Unassigned</span>
                            </div>
                            
                            <!-- Device Name -->
                            <div class="d-flex align-center mt-1">
                              <v-icon small class="mr-1">mdi-desktop-classic</v-icon>
                              <span class="caption">{{ device && (device.hostname || device.name) || 'Unknown Device' }}</span>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Action Hint -->
                        <v-divider class="my-2" v-if="isOnline && allowLiveView"></v-divider>
                        <div class="text-center caption" v-if="isOnline && allowLiveView">
                          <v-icon x-small>mdi-mouse</v-icon>
                          Click to open Live View
                        </div>
                      </v-card-text>
                    </v-card>
                  </template>
                </app-online-device-wrapper>
              </div>
            </v-scroll-y-transition>
          </template>

          <!--Time Performance-->
          <app-divider label="Time / Activity Breakdown" class="pt-4" show-expanded :expanded.sync="timePerformanceShown"></app-divider>
          <v-scroll-y-transition hide-on-leave>
            <div style="width: 100%; max-height: 500px" class="px-12 pt-4" v-if="timePerformanceShown">
              <apex-chart height="300" type="line" :options="timePerformanceChartData.chartOptions"
                          :series="timePerformanceChartData.series"></apex-chart>
            </div>
          </v-scroll-y-transition>
          <!--Breakdown-->
          <app-divider label="App Breakdown" class="pt-4" show-expanded :expanded.sync="breakdownShown"></app-divider>

          <template v-if="dateDays !== 0">
            <div class="text-center overline amber--text">Breakdown is only available for a single day</div>
          </template>
          <template v-else>
            <v-scroll-y-transition hide-on-leave>
              <div style="width: 100%; max-height: 500px" class="px-12 pt-4" v-if="breakdownShown">
                <v-scroll-x-transition hide-on-leave>
                  <div v-if="loadingBreakdown" style="height: 300px" class="d-flex align-center justify-center">
                    <app-centre-loader label="Loading"></app-centre-loader>
                  </div>
                </v-scroll-x-transition>
                <v-scroll-x-transition hide-on-leave>
                  <apex-chart
                    v-if="stats.breakdown && !loadingBreakdown"
                    height="300"
                    type="rangeBar"
                    :options="breakdownChartData.chartOptions"
                    :series="breakdownChartData.series"
                  ></apex-chart>
                </v-scroll-x-transition>
              </div>
            </v-scroll-y-transition>
          </template>
          <!--Top Apps-->
          <app-divider label="Top Apps" show-expanded :expanded.sync="topAppsShown" class="pt-4"></app-divider>
          <v-scroll-y-transition hide-on-leave>
            <div class="d-flex flex-wrap flex-grow-1" v-if="topAppsShown">
              <div class="flex-grow-1 flex-equal " style="min-width: 500px;">
                
                <v-data-table
                  :headers="topAppTableHeadersWithAccess"
                  :items="stats.topApps"
                  :items-per-page="stats.topApps.length"
                  hide-default-footer
                  class=""
                  :sortBy.sync="$store.state.sortPreference.employeeStats.topApps.by"
                  :sort-desc.sync="$store.state.sortPreference.employeeStats.topApps.order"
                  @click:row="gotoAppStats($event.match)"
                >

                  <template v-slot:item.match="{ item }">
                    <div class="d-flex align-center overline">
                      <app-icon :match="item.match" left small/>
                      {{ item.match }}
                    </div>
                  </template>

                  <template v-slot:item.totalDuration="{ item }">{{hourDisplay(item.totalDuration, true) }}</template>
                  
                  <template v-slot:item.label="{ item }">
                    <div class="d-flex align-center">
                      <v-icon small :color="labelsRef[item.label].color" left>
                        {{ labelsRef[item.label].icon }}
                      </v-icon>
                      <div class="body-2 d-flex align-center" :class="`${labelsRef[item.label].color}--text`">{{
                          labelsRef[item.label].text
                        }}
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      color="primary"
                      class="mx-2"
                      small
                      icon
                      :loading="loadingClassifications.includes(item.match)"
                      @click.stop="classificationMenuClicked($event, item)"
                    >
                      <v-icon>mdi-cog</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
              <div class="d-flex justify-center fill-height flex-grow-1 flex-equal "
                   style="min-width: 500px">
                <div class="flex-grow-1 flex-equal">
                  <div class="text-center subtitle-1">
                    Top Apps Usage
                  </div>
                  <apex-chart type="donut" :options="topAppsChartData.chartOptions"
                              :series="topAppsChartData.seriesData"></apex-chart>
                </div>
                <div class="flex-grow-1 flex-equal">
                  <div class="text-center subtitle-1">
                    Productivity Distribution
                  </div>
                  <apex-chart type="radialBar" :options="productivityChartData.chartOptions"
                              :series="productivityChartData.seriesData"></apex-chart>
                </div>
              </div>
            </div>
          </v-scroll-y-transition>
        </div>
      </div>
    </v-scroll-x-transition>

    <app-dlg
      v-if="$store.state.ref.employeeStats.showCardsSelection"
      :show-dlg.sync="$store.state.ref.employeeStats.showCardsSelection"
      title="Select Columns"
      :default-padding="false"
    >

      <app-list
        v-model="$store.state.ref.employeeStats.selectedCards"
        :items="cards"
        :multiple="true"
        headline-key="text"
        item-key="value"
        sub-line-key="description"
        :show-check-boxes="true"
        :return-object="false"
        class="fill-height of-y"
        :height="400"
        @input="updateSelections"
      />

      <template v-slot:actions>
        <v-btn
          @click="$store.state.ref.employeeStats.selectedCards = [...$store.state.defaultSelections.employeeStats]; updateSelections()"
          small
          class="ml-2"
          color="blue"
          outlined
          size="small"
        >
          Reset Selections
        </v-btn>
        <v-btn
          @click="$store.state.ref.employeeStats.showCardsSelection = false"
          small
          class="ml-2"
          color="secondary"
          variant="contained"
          size="small"
        >
          Done
        </v-btn>
      </template>
    </app-dlg>

    <v-menu
      v-if="showClassificationMenu"
      v-model="showClassificationMenu"
      :position-x="classificationX"
      :position-y="classificationY"
      absolute
      offset-y
      :max-width="200"
      :min-width="200"
    >
      <v-card>
        <v-list dense>
          <v-list-item-group
            :value="selectedClassification.label"
            color="primary"
          >
            <v-list-item
              v-for="label in labels"
              :value="label.id"
              :key="label.id"
              @click="changeLabel(label.id)"
              active-color="primary"
            >
              <v-list-item-content>
                <v-list-item-title v-text="label.text"></v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <v-list-item
            @click="gotoApp(selectedClassification.match)"
            active-color="primary"
          >
            <v-list-item-content>
              <v-list-item-title>More Options</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <app-classification-dlg
      ref="classificationDlg"
      @future="saveClassificationLabelChangesConfirm()"
      @history="saveClassificationLabelChangesConfirm(true)"
    />

    <app-add-leave-dlg
      v-if="access.leave"
      :shown.sync="$store.state.ref.employeeStats.showAddLeaveRecord"
      :preset-start="this.$store.state.ref.dashboard.selectedRange.start"
      :preset-employee-id="employee._id"
      @input="leaveRecorded"
    />
  </div>
</template>

<script>

import AppCentreLoader from "@/components/Shared/AppCentreLoader";
import {
  percentDisplay,
  hourDisplay,
  durationDisplay,
  isValidUrl,
  arrSplice,
  durationDisplayFull
} from "@/core/codeFunctions";
import AppDlg from "@/components/Shared/AppDlg";
import AppList from "@/components/Shared/AppList/AppList";
import {mapActions, mapGetters} from "vuex";
import AppDivider from "@/components/Shared/AppDivider/AppDivider";
import AppOutlinedChip from "@/components/Shared/AppOutlinedChip";
import VueApexCharts from 'vue-apexcharts'
import {differenceInDays, format, isSameDay, isSameMonth, isSameYear} from "date-fns";
import AppScreenshotPreview from "@/components/Shared/Screenshots/AppScreenshotPreview";
import AppIcon from "@/components/Shared/AppIcon";
import {StatusMap} from "@/models/statusMap";
import AppClassificationDlg from "@/components/Shared/AppClassificationDlg";
import AppAddLeaveDlg from "@/components/Leave/AppAddLeaveDlg.vue";
import AppOnlineDeviceWrapper from "@/components/Shared/AppOnlineDeviceWrapper.vue";
import AppLiveView from "@/components/Shared/AppLiveView.vue";

export default {
  components: {
    AppLiveView,
    AppOnlineDeviceWrapper,
    AppAddLeaveDlg,
    AppClassificationDlg,
    AppIcon,
    AppScreenshotPreview,
    AppOutlinedChip,
    AppDivider,
    AppList,
    AppDlg,
    AppCentreLoader,
    apexChart: VueApexCharts
  },
  props: {},
  data() {
    return {
      liveViewDeviceId: null,
      isValidUrl,
      screenshotsShown: true,
      onlineDevicesShown: true,
      statisticsShown: true,
      topAppsShown: true,
      timePerformanceShown: true,
      breakdownShown: true,
      hourDisplay,
      percentDisplay,
      durationDisplay,
      loading: false,
      loadingBreakdown: false,
      
      stats: {
        summary: null,
        topApps: [],
        breakdown: [],
        timePerformance: []
      },
      
      cards: [
        {text: 'Active Time', value: 'activeTime', description: 'Total time active'},
        {text: 'Productive', value: 'ptt', description: 'Total time productive'},
        {text: 'Productive Normal', value: 'ptn', description: 'Time productive during working hours'},
        {text: 'Productive Overtime', value: 'pto', description: 'Time productive outside of working hours'},
        {text: 'Unproductive', value: 'utt', description: 'Total time unproductive'},
        {text: 'Unproductive Normal', value: 'utn', description: 'Time unproductive during working hours'},
        {
          text: 'Unproductive Overtime',
          value: 'uto',
          description: 'Time unproductive outside of working hours'
        },
        {text: 'Neutral', value: 'ntt', description: 'Total time neutral'},
        {text: 'Neutral Normal', value: 'ntn', description: 'Time neutral during working hours'},
        {text: 'Neutral Overtime', value: 'nto', description: 'Time neutral outside of working hours'},
        {text: 'Target', value: 'ttt', description: 'Total target duration'},
        {text: 'Idle', value: 'idleTime', description: 'Total idle duration'},
        {text: 'Productive Target %', value: 'productivityTarget', description: 'Total productivity vs total target'},
        {text: 'Productive Normal Target %', value: 'productivityNormalTarget', description: 'Total productivity in working hours vs total target'},
        {
          text: 'Productive Active %',
          value: 'productivityActive',
          description: 'Total productivity vs total active'
        },
      ],
      
      topAppTableHeaders: [
        {text: 'Name', value: 'match'},
        {text: 'Total Duration', value: 'totalDuration'},
        {text: 'Label', value: 'label'},
      ],

      loadingClassifications: [],
      showClassificationMenu: false,
      selectedClassification: null,
      selectedClassificationNewLabel: null,
      classificationX: 0,
      classificationY: 0,
    }
  },
  watch: {
    "$store.state.ref.dashboard.selectedRange.start": {
      immediate: true,
      handler(val) {
        this.refreshData()
      }
    },
    devicesWithAccessIds() {
      this.refreshData()
    }
  },
  mounted() {
    // this.refreshData();
  },
  methods: {
    ...mapActions(['gotoTeam', 'gotoDeviceSettings', 'gotoAppStats', 'gotoApp', 'updateSelections']),

    leaveRecorded() {
      this.refreshData();
    },

    async changeLabel(labelId) {
      this.selectedClassificationNewLabel = labelId

      if (this.$store.state.historyPreference)
        this.saveClassificationLabelChangesConfirm(this.$store.state.historyPreference === 'history').catch(console.log)
      else {
        this.$refs.classificationDlg.showDlg()
      }
    },

    async saveClassificationLabelChangesConfirm(updateHistorical = false) {
      const selectedClassification = this.selectedClassification
      const loadingMatch = selectedClassification.match
      const defaultLabel = this.selectedClassificationNewLabel
      try {
        this.loadingClassifications.push(selectedClassification.match)
        const editMap = new StatusMap({match: selectedClassification.match, defaultLabel: defaultLabel})
        const {_id} = await editMap.updateDefault(updateHistorical);
        selectedClassification.label = defaultLabel
        arrSplice(this.loadingClassifications, (match) => match === loadingMatch)
        if (updateHistorical)
          this.refreshData(false)
      } catch (e) {
        this.$snack.netWorkError()
        console.log(e)
        arrSplice(this.loadingClassifications, (match) => match === loadingMatch)
      }
    },

    classificationMenuClicked(e, classification) {
      this.selectedClassification = classification;
      const params = e.target.getBoundingClientRect();
      this.showClassificationMenu = false
      this.$nextTick(() => {
        this.classificationX = (params.left + params.width) - 200
        this.classificationY = params.bottom
        this.showClassificationMenu = true
      })
    },

    async refreshData(enableLoading = true) {
      try {
        if (enableLoading)
          this.loading = true;
        const {summary, topApps, timePerformance} = await this.$newReq('POST', 'dashboard/getEmployeeStats', {
          start: this.start,
          end: this.end,
          deviceIds: this.devices.map(obj => obj._id).filter(deviceId => this.devicesWithAccessIds.includes(deviceId))
        })

        this.stats = {
          summary,
          topApps,
          breakdown: this.stats.breakdown || null,
          timePerformance
        }
        if (this.dateDays === 0)
          this.refreshBreakdownData(enableLoading)
      } catch (e) {
        console.log(e)
        this.$snack.netWorkError();
      } finally {
        if (enableLoading)
          this.loading = false
      }
    },

    async refreshBreakdownData(enableLoading = true) {
      try {
        if (enableLoading)
          this.loadingBreakdown = true;
        const breakdown = await this.$newReq('POST', 'dashboard/getEmployeeStats/breakdown', {
          start: this.start,
          end: this.end,
          deviceIds: this.devices.map(obj => obj._id).filter(deviceId => this.devicesWithAccessIds.includes(deviceId))
        })

        let min = null;
        let max = null;

        const breakDownMapped = breakdown.map(app => {
          const appVal = {
            name: app.name,
            data: app.data.map(val => {
              if (!min || new Date(val.y[0]) < min)
                min = new Date(val.y[0])

              if (!max || new Date(val.y[1]) > max)
                max = new Date(val.y[1])

              let x;
              let fillColor;
              switch (val.x) {
                case -1: {
                  x = "Idle";
                  fillColor = '#9E9E9E'
                  break;
                }
                case -2: {
                  x = "Voice Call"
                  fillColor = '#CDDC39'
                  break;
                }
                case -3: {
                  x = "Manual Time"
                  fillColor = this.labelsRef[val.mtLabel].cssColor
                  break;
                }
                case -4: {
                  x = "Microsoft Teams"
                  fillColor = '#4b53b8'
                  break;
                }
                default: {
                  x = this.labelsRef[val.x].text
                  fillColor = this.labelsRef[val.x].cssColor
                }
              }

              return {
                label: val.x,
                x: x,
                y: val.y.map(dateStr => {
                  return new Date(dateStr).getTime()
                }),
                fromNo: val.fromNo || null,
                toNo: val.toNo || null,
                mtCreated: val.mtCreated ? format(new Date(val.mtCreated), "yyyy-MM-dd HH:mm") : null,
                mtApprovedBy: (val.mtApprovedBy ? this.usersRef[val.mtApprovedBy]?.name || val.mtApprovedBy : null) || null,
                mtApprovedDate: val.mtApprovedDate ? format(new Date(val.mtApprovedDate), "yyyy-MM-dd HH:mm") : null,
                
                teamsOrganiser: val.teamsOrganiser || null,
                teamsParticipants: val.teamsParticipants || null,
                teamsMeetingStart: val.teamsMeetingStart ? format(new Date(val.teamsMeetingStart), "yyyy-MM-dd HH:mm") : null,
                teamsMeetingEnd: val.teamsMeetingEnd ? format(new Date(val.teamsMeetingEnd), "yyyy-MM-dd HH:mm") : null,
                teamsMeetingType: val.teamsMeetingType || null,
                
                mtLabel: val.mtLabel,
                fillColor: fillColor
              }
            })

          }
          return appVal
        })

        this.stats.breakdown = breakDownMapped.sort((a,b) => a.data[0].x > b.data[0].x ? -1 : 0)
      } catch (e) {
        console.log(e)
        this.$snack.netWorkError();
      } finally {
        if (enableLoading)
          this.loadingBreakdown = false
      }
    }
  },
  computed: {

    ...mapGetters(['usersRef', 'teamsRef', 'employeesRef', 'devicesByEmployee', 'labelsRef', 'labels', 'access', 'devicesWithAccessIds', 'devicesRef']),

    dateDays() {
      return differenceInDays(this.end, this.start)
    },

    topAppTableHeadersWithAccess() {
      const cols = this.topAppTableHeaders
      return this.access.classification
        ? cols.concat({text: '', value: 'actions', sortable: false, align: 'center', width: 30})
        : cols
    },

    timePerformanceChartData() {

      const timeNames = this.stats.timePerformance.map(obj => {
        // return obj._id % 1800 === 0 ? obj.name : ''
        return obj.name
      })

      const neutralSeries = this.stats.timePerformance.map(obj => obj.ntt)
      const productiveSeries = this.stats.timePerformance.map(obj => obj.ptt)
      const unproductiveSeries = this.stats.timePerformance.map(obj => obj.utt)

      const series = [
        {
          name: 'Neutral',
          data: neutralSeries,
          fillColor: this.labelsRef[0].cssColor
        }, {
          name: 'Productive',
          data: productiveSeries,
          fillColor: this.labelsRef[1].cssColor
        }, {
          name: 'Unproductive',
          data: unproductiveSeries,
          fillColor: this.labelsRef[2].cssColor
        }
      ]

      const dummySeries = [{
        name: "Desktops",
        data: [10, 41, 35, 51, 49, 62, 69, 91, 148]
      }]

      const chartOptions = {
        chart: {
          height: 350,
          type: 'line',
          zoom: {enabled: false},
          toolbar: {
            tools: {
              download: false,
            },
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          colors: [this.labelsRef[0].cssColor, this.labelsRef[1].cssColor, this.labelsRef[2].cssColor],
          dashArray: [0, 0, 0],
          width: 2
        },
        grid: {
          show: true,
          borderColor: 'var(--v-primary-base)',
          strokeDashArray: 0,
          position: 'back',
          yaxis: {
            lines: {
              show: false
            }
          },
        },
        xaxis: {
          categories: timeNames,
          labels: {
            rotate: -60,
            rotateAlways: true,
            style: {
              colors: timeNames.map(() => '#fff'),
            },
          },
          axisBorder: {
            show: true,
            color: 'var(--v-primary-base)',
            height: 1,
          },
        },
        tooltip: {
          enabled: false,
          theme: 'dark',
          y: {
            formatter: function (val) {
              return durationDisplay(val)
            },
            title: {
              formatter: (seriesName) => seriesName,
            },
          },
        },
        yaxis: {
          show: true,
          title: {
            text: 'Activity'
          },
          labels: {
            show: false,
          },
        },
        legend: {
          markers: {
            fillColors: [this.labelsRef[0].cssColor, this.labelsRef[1].cssColor, this.labelsRef[2].cssColor]
          },
          labels: {
            colors: ['#fff']
          },
        },
        colors: [this.labelsRef[0].cssColor, this.labelsRef[1].cssColor, this.labelsRef[2].cssColor],
      }

      return {
        series,
        chartOptions,
      }
    },

    breakdownChartData() {
      const vm = this;
      const chartOptions = {
        grid: {
          show: false,},
        yaxis: {
          labels: {
            show: true,
            align: 'right',
            style: {
              colors: ['#FFF'],
              fontSize: '14px',
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            },
          },
        },
        chart: {
          height: 350,
          type: 'rangeBar',
          animations: {
            enabled: false
          },
          toolbar: {
            tools: {
              download: false,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '85%',
            rangeBarGroupRows: true
          }
        },
        fill: {
          type: 'solid'
        },
        xaxis: {
          type: 'datetime',
          axisBorder: {
            show: true,
            color: 'var(--v-primary-base)',
            height: 1,
          },
          labels: {
            style: {
              colors: ['#fff'],
            },
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
        },
        legend: {
          position: 'right',
          labels: {
            colors: ['#FFF']
          },
          markers: {
            fillColors: this.stats.breakdown.map(app => {
              return app.data[0].label === -1
                ? '#9E9E9E'
                : (
                  app.name === 'Voice Call'
                    ? '#CDDC39'
                    : (
                        (app.name === 'Teams Meeting' || app.name === 'Teams Call')
                            ? '#4b53b8'
                            : this.labelsRef[app.data[0].mtLabel].cssColor
                      )
                )
            }),
          },
        },
        tooltip: {
          custom: function({series, seriesIndex, dataPointIndex, w}) {
            const item = vm.stats.breakdown[seriesIndex];
            const start = item.data[dataPointIndex].y[0];
            const end = item.data[dataPointIndex].y[1];
            return`<div class="pa-2" style="color: white; background: black">
                    <div class="caption">${item.name}</div>
                    ${item.data[dataPointIndex].fromNo ? `<div class="caption">From: ${item.data[dataPointIndex].fromNo}</div>` : ''}
                    ${item.data[dataPointIndex].toNo ? `<div class="caption">To: ${item.data[dataPointIndex].toNo}</div>` : ''}
                    ${item.data[dataPointIndex].mtCreated ? `<div class="caption">Logged: ${item.data[dataPointIndex].mtCreated}</div>` : ''}
                    ${item.data[dataPointIndex].mtApprovedBy ? `<div class="caption">Approved By: ${item.data[dataPointIndex].mtApprovedBy}</div>` : ''}
                    ${item.data[dataPointIndex].mtApprovedDate ? `<div class="caption">Approved: ${item.data[dataPointIndex].mtApprovedDate}</div>` : ''}
                    
                    ${item.data[dataPointIndex].teamsOrganiser ? `<div class="caption">Organiser: ${item.data[dataPointIndex].teamsOrganiser}</div>` : ''}
                    ${item.data[dataPointIndex].teamsParticipants?.length ? `<div class="caption">Participants: <ul>${item.data[dataPointIndex].teamsParticipants.map(v => `<li>${v}</li>`).join('')}</ul></div>` : ''}
                    ${item.data[dataPointIndex].teamsMeetingStart ? `<div class="caption">Meeting Start: ${item.data[dataPointIndex].teamsMeetingStart}</div>` : ''}
                    ${item.data[dataPointIndex].teamsMeetingEnd ? `<div class="caption">Meeting End: ${item.data[dataPointIndex].teamsMeetingEnd}</div>` : ''}
<!--                    teamsOrganiser-->
<!--teamsParticipants-->

<!--teamsMeetingType-->
                    
                    <div class="body-1 text-right">${durationDisplay(Math.floor((end - start) / 1000))}</div>
                    </div>
                    `
          }
        }
      }
      return {
        chartOptions,
        series: this.stats.breakdown
      }
    },

    productivityChartData() {


      const totalTime = this.stats.summary.ptt
        + this.stats.summary.ntt
        + this.stats.summary.utt
        + this.stats.summary.idleTime

      const series = [
        {
          label: 'Productive',
          val: Math.floor((this.stats.summary.ptt / totalTime) * 100) > 0 ? Math.floor((this.stats.summary.ptt / totalTime) * 100) || 0.2 : 0.2,
          color: this.labelsRef[1].cssColor
        },
        {
          label: 'Neutral',
          val: Math.floor((this.stats.summary.ntt / totalTime) * 100) > 0 ? Math.floor((this.stats.summary.ntt / totalTime) * 100) || 0.2 : 0.2,
          color: this.labelsRef[0].cssColor
        },
        {
          label: 'Unproductive',
          val: Math.floor((this.stats.summary.utt / totalTime) * 100) > 0 ? Math.floor((this.stats.summary.utt / totalTime) * 100) || 0.2 : 0.2,
          color: this.labelsRef[2].cssColor
        },
        {
          label: 'Idle',
          val: Math.floor((this.stats.summary.idleTime / totalTime) * 100) > 0 ? Math.floor((this.stats.summary.idleTime / totalTime) * 100) || 0.2 : 0.2,
          color: '#607D8B'
        },

      ]

      const seriesNames = series.map(obj => obj.label)
      const seriesData = series.map(obj => obj.val)
      const seriesColors = series.map(obj => obj.color)

      const chartOptions = {
        chart: {
          type: 'radialBar',
        },
        plotOptions: {
          radialBar: {
            inverseOrder: true,
            hollow: {
              margin: 10,
              size: '46%',
              background: 'transparent',
            },
            track: {
              show: false,
            },
            startAngle: -180,
            endAngle: 180
          },
        },
        fill: {
          type: 'solid',
          colors: seriesColors
        },
        stroke: {
          lineCap: 'round'
        },
        // series: seriesData,
        labels: seriesNames,
        legend: {
          show: true,
          floating: false,
          position: 'top',
          // offsetX: 70,
          // offsetY: 208,
          labels: {
            colors: ['#fff']
          },
          markers: {
            fillColors: seriesColors
          }
        },
        // fill: {
        //     opacity: 1,
        //     colors: seriesColors
        // },

        // labels: seriesNames,
        // grid: {
        //     show: false,
        // },
        // dataLabels: {
        //     enabled: false,
        //     background: {
        //         enabled: false,},
        //     style: {colors: ['#fff']},
        //     dropShadow: {enabled: false,},
        //     formatter: function (val, opts) {
        //         return hourDisplay(seriesData[opts.seriesIndex])
        //     },
        // },
        // stroke: {
        //     opacity: 0,
        //     colors: seriesColors,
        //     width: 0
        // },

        // chart: {
        //     type: 'polarArea',
        // },
        // legend: {show: false,},
        // yaxis: {show: false},
        // plotOptions: {
        //     polarArea: {
        //         rings: {
        //             strokeWidth: 0
        //         },
        //         spokes: {
        //             strokeWidth: 0
        //         },
        //     }
        // },
        // tooltip: {
        //     fillSeriesColor: true,
        //     custom: function({series, seriesIndex, dataPointIndex, w}) {
        //         console.log({series, seriesIndex, dataPointIndex, w})
        //         return` <div class="pa-1"> <div class="caption">${seriesNames[seriesIndex]}</div> <div class="body-1 text-right">${durationDisplay(seriesData[seriesIndex])}</div> </div> `
        //     }
        // },
        // theme: {
        //     mode: 'dark',}
      }
      return {
        seriesData: seriesData,
        chartOptions,
      }
    },

    topAppsChartData() {

      const series = [...this.stats.topApps].sort((a, b) => a.label > b.label ? 1 : -1)

      const seriesNames = series.map(obj => obj.match)
      const seriesData = series.map(obj => obj.totalDuration)
      const seriesColors = series.map(obj => this.labelsRef[obj.label].cssColor)

      const chartOptions = {
        labels: seriesNames,
        grid: {
          show: false,
        },
        dataLabels: {
          enabled: false,
          background: {
            enabled: false,
          },
          style: {colors: ['#fff']},
          dropShadow: {enabled: false,},
          formatter: function (val, opts) {
            return hourDisplay(seriesData[opts.seriesIndex])
          },
        },
        stroke: {
          opacity: 0,
          colors: ['#000'],
          width: 0
        },
        fill: {
          opacity: 1,
          colors: seriesColors
        },
        // chart: {
        //     type: 'pie',
        // },
        legend: {show: false,},
        yaxis: {show: false},
        plotOptions: {

        },
        tooltip: {
          fillSeriesColor: true,
          custom: function ({series, seriesIndex, dataPointIndex, w}) {
            return ` <div class="pa-1"> <div class="caption">${seriesNames[seriesIndex]}</div> <div class="body-1 text-right">${durationDisplay(seriesData[seriesIndex])}</div> </div> `
          }
        },
        // theme: {
        //     mode: 'dark',}
      }
      return {
        seriesData,
        chartOptions,
      }
    },

    shownCards() {
      return this.$store.state.ref.employeeStats.selectedCards.filter(card => card !== 'productivityWorkTime' && card !== "workTime").map(key => {
        const card = {
          key,
          info: this.cards.filter(card => card.value === key).pop(),
          val: (() => {
            switch (key) {
              case 'productivityTarget':
                return percentDisplay(this.stats.summary[key])
              case 'productivityActive':
                return percentDisplay(this.stats.summary[key])
              case 'productivityNormalTarget':
                return percentDisplay(this.stats.summary[key])
              default:
                return durationDisplayFull(this.stats.summary[key])
            }
          })(),
          // valDesc: (() => {
          //     if (['productivityTarget', 'productivityActive'].includes(key))
          //         return null
          //     return hourDisplay(this.stats.summary[key])
          // })(),
        }
        return card
      })
    },

    team() {
      return this.teamsRef[this.employee.teamId]
    },

    devices() {
      return this.devicesByEmployee[this.$store.state.ref.employeeStats.selectedEmployee];
    },

    employee() {
      return this.employeesRef[this.$store.state.ref.employeeStats.selectedEmployee];
    },
    start() {
      return this.$store.state.ref.dashboard.selectedRange.start
    },
    end() {
      return this.$store.state.ref.dashboard.selectedRange.end
    },
    dateTitle() {
      // TODO Add Here
      // if (this.range.title)
      //     return this.range.title
      if (!isSameYear(this.range.start, this.range.end))
        return `${format(this.range.start,'dd MMM yyyy')} - ${format(this.range.end,'dd MMM yyyy')}`
      if (!isSameMonth(this.range.start, this.range.end))
        return `${format(this.range.start,'dd MMM')} - ${format(this.range.end,'dd MMM yyyy')}`
      if (!isSameDay(this.range.start, this.range.end))
        return `${format(this.range.start,'dd')} - ${format(this.range.end,'dd MMM yyyy')}`
      return format(this.range.end,'dd MMM yyyy')
    },
    range() {
      return this.$store.state.ref.dashboard.selectedRange
    },
  },
}
</script>

<style scoped>

</style>